import type { SessionView } from '~/helpers/learner/session-view'
import { Link } from 'react-router-dom'
import { Badge } from '~/components/ui/badge'
import { completionStatus } from '~/helpers/learner/session-view'
import ModuleArtwork from '~/pages/scorm/components/ModuleArtwork'
import { useStatusMeta } from '~/pages/scorm/components/useStatusMeta'

/**
 * Le cadre commun aux deux sortes de carte : bandeau visuel, type et état, titre,
 * contenu, actions.
 *
 * Les cartes d'une rangée ont la même hauteur (#307) : les actions sont calées
 * en bas (`mt-auto`), si bien que l'espace libre tombe au-dessus des boutons et
 * non au milieu du contenu.
 *
 * Seule sur sa rangée, la grille lui donne toute la largeur : une requête de
 * conteneur la couche alors à l'horizontale, le visuel à gauche, plutôt que
 * d'étirer une carte verticale sur toute la page.
 *
 * Avec `href`, toute la carte y mène (#91) : le titre est son seul lien, étiré
 * sur la carte par un pseudo-élément. Envelopper la carte dans un `<a>` en
 * ferait l'intitulé de tout son contenu et y imbriquerait les boutons ; ceux-ci
 * restent au-dessus du lien étiré et gardent leur propre action.
 */
function SessionCardFrame({
  visualId,
  kindLabel,
  session,
  subtitle,
  href,
  children,
  actions,
}: {
  visualId: string
  kindLabel: string
  session: SessionView
  subtitle?: string | null
  href?: string
  children?: React.ReactNode
  actions: React.ReactNode
}) {
  const meta = useStatusMeta()(completionStatus(session))

  return (
    <article className="@container relative h-full overflow-hidden rounded-2xl border border-border bg-card transition-shadow hover:shadow-md has-[a[data-card-link]:focus-visible]:ring-2 has-[a[data-card-link]:focus-visible]:ring-ring">
      <div className="flex h-full flex-col @3xl:flex-row">
        <ModuleArtwork moduleId={visualId} className="h-16 w-full shrink-0 @3xl:h-auto @3xl:w-48" />

        <div className="flex flex-1 flex-col gap-3 p-4 sm:p-5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">{kindLabel}</span>
            <Badge variant={meta.badgeVariant} className="shrink-0">{meta.badgeLabel}</Badge>
          </div>

          <div className="min-w-0">
            <h3 className="font-heading text-lg leading-tight text-balance">
              {href
                ? (
                    <Link to={href} data-card-link className="outline-none after:absolute after:inset-0">
                      {session.name}
                    </Link>
                  )
                : session.name}
            </h3>
            {subtitle && (
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>

          {children}

          {/* Couchée, la carte cale ses boutons à droite plutôt que de les étirer sur toute sa largeur. */}
          <div className="relative z-10 mt-auto flex flex-wrap gap-2 pt-1 @3xl:justify-end @3xl:*:flex-none">{actions}</div>
        </div>
      </div>
    </article>
  )
}

export default SessionCardFrame
